// src/components/tasks/EmptyTaskState.jsx
import React from 'react';
import { Box, Button } from '@mui/material';
import { Add as AddIcon, ViewList as EmptyIcon } from '@mui/icons-material';
import { EmptyState } from '../core';
import useProjectPermissions from '../../hooks/useProjectPermissions';

/**
 * EmptyTaskState - Component to display when no tasks or sections exist
 * Uses the standardized EmptyState component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.hasActiveFilters - Whether active filters are applied
 * @param {boolean} props.canEdit - DEPRECATED: Using permissions hook instead
 * @param {Function} props.onCreateTask - Function to create a task
 * @param {Function} props.onAddSection - Function to add a section
 * @param {string} props.projectId - Project ID for permission checks
 */
const EmptyTaskState = ({
  hasActiveFilters,
  canEdit = false, // Deprecated but kept for backward compatibility
  onCreateTask,
  onAddSection,
  projectId
}) => {
  // Use the unified permissions hook instead of separate auth checks
  const {
    canCreateTask,
    canCreateSection
  } = useProjectPermissions(projectId);

  // Determine if actions should be shown
  // If projectId is not provided, fallback to the canEdit prop for backward compatibility
  const showActions = projectId ? (canCreateTask || canCreateSection) : canEdit;

  const handleCreateTask = () => {
    onCreateTask({
      title: 'New task',
      status: 'to_do',
      sectionId: null
    });
  };

  // Prepare action buttons for the empty state
  const renderActionButtons = () => {
    if (!showActions || hasActiveFilters) return null;

    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        {(projectId ? canCreateTask : true) && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTask}
          >
            Add Task
          </Button>
        )}

        {(projectId ? canCreateSection : true) && (
          <Button
            variant="outlined"
            onClick={onAddSection}
          >
            Add Section
          </Button>
        )}
      </Box>
    );
  };

  return (
    <EmptyState
      title={hasActiveFilters ? 'No matching tasks' : 'No tasks or sections found'}
      description={
        hasActiveFilters
          ? 'Try changing your filters or search query to find what you\'re looking for.'
          : 'Create your first task or section to get started with your project.'
      }
      icon={<EmptyIcon />}
      action={renderActionButtons()}
      filtered={hasActiveFilters}
      variant="default"
      magical={false}
      minHeight="100%"
    />
  );
};

export default EmptyTaskState;